/**
 * pccc-field.js
 *
 * Drives every instance of snippets/pccc-field.liquid on the page —
 * product form, cart drawer and cart page — from a single source of
 * truth, so typing in one mirrors into the others and the gate on the
 * checkout buttons is consistent everywhere.
 *
 * The gate requires a well-formed PCCC (개인통관고유부호): the letter P
 * followed by 12 digits. Only normalised, well-formed codes are ever
 * written to the PCCC cart attribute.
 *
 * Install: assets/pccc-field.js
 *   <script src="{{ 'pccc-field.js' | asset_url }}" defer="defer"></script>
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'atelier:pccc';
  var SYNC_DELAY = 400;
  var PCCC_PATTERN = /^P\d{12}$/;

  var value = '';
  var lastSynced = null;
  var syncTimer = null;
  var suppress = false;
  var started = false;

  var ADD_SELECTORS = [
    'button[name="add"]',
    '.add-to-cart-button',
    '.product-form__submit'
  ];

  var CHECKOUT_SELECTORS = [
    '[name="checkout"]',
    '.cart-checkout-button'
  ];

  var DYNAMIC_SELECTORS = [
    '.shopify-payment-button',
    '.shopify-payment-button__wrapper',
    '[data-shopify="payment-button"]',
    '.cart-dynamic-checkout-buttons',
    '.additional-checkout-buttons'
  ];

  /* ------------------------------------------------------------------ */

  function wrapper() {
    return document.querySelector('[data-pccc-wrapper]');
  }

  function inputs() {
    return document.querySelectorAll('[data-pccc-input]');
  }

  function flag(name) {
    var el = wrapper();
    return !!el && el.dataset[name] === 'true';
  }

  function isLoggedIn() { return flag('loggedIn'); }

  // Customers paste codes with spaces or hyphens, and the customs
  // office prints the leading P uppercase. Compare and store the
  // canonical form; leave the raw keystrokes in the input.
  function normalize(v) {
    return (v || '').replace(/[\s\-\u2010-\u2015]/g, '').toUpperCase();
  }

  function isPresent() { return value.trim().length > 0; }

  function isValid() { return PCCC_PATTERN.test(normalize(value)); }

  function readSession() {
    try { return sessionStorage.getItem(STORAGE_KEY) || ''; }
    catch (e) { return ''; }
  }

  function writeSession(v) {
    try { sessionStorage.setItem(STORAGE_KEY, v); } catch (e) {}
  }

  function clearSession() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  function collect(selectors) {
    var found = [];
    selectors.forEach(function (sel) {
      Array.prototype.forEach.call(document.querySelectorAll(sel), function (el) {
        if (found.indexOf(el) === -1) found.push(el);
      });
    });
    return found;
  }

  /* ------------------------------------------------------------------ */

  // Must be locale-aware. Shopify.routes.root is '/' or '/en/'; posting to a
  // hardcoded '/cart/update.js' from an /en/ page gets redirected, and the
  // redirect downgrades the POST to a GET, so the attribute is silently never
  // written and the PCCC never reaches checkout.
  function cartRoute(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }

  function updateCartAttribute(next) {
    return fetch(cartRoute('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { PCCC: next } })
    }).then(function (response) {
      if (!response.ok) throw new Error('PCCC sync failed: ' + response.status);
      return response;
    });
  }

  function sync(next, immediate) {
    if (next === lastSynced) return;

    clearTimeout(syncTimer);

    var run = function () {
      lastSynced = next;
      updateCartAttribute(next).catch(function () {
        lastSynced = null;
      });
    };

    if (immediate) run();
    else syncTimer = setTimeout(run, SYNC_DELAY);
  }

  // A code left in the cart by an earlier visitor on this device, or one
  // that no longer passes the format check. The field renders empty or
  // blocked, so drop it from the cart too or it would still travel to
  // checkout unseen.
  function clearStaleCartAttribute() {
    if (!flag('cartHasPccc')) {
      lastSynced = '';
      return;
    }

    Array.prototype.forEach.call(
      document.querySelectorAll('[data-pccc-wrapper]'),
      function (el) { el.dataset.cartHasPccc = 'false'; }
    );

    sync('', true);
  }

  /* ------------------------------------------------------------------ */

  function apply() {
    var valid = isValid();
    var present = isPresent();

    suppress = true;

    Array.prototype.forEach.call(inputs(), function (input) {
      if (input.value !== value) input.value = value;
      input.classList.toggle('pccc-invalid', !valid);
      input.setAttribute('aria-invalid', valid ? 'false' : 'true');
    });

    // Both messages show on load, unprompted: the buttons are already
    // disabled at that point, so the field has to say why.
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-pccc-error="required"]'),
      function (el) { el.hidden = present; }
    );

    Array.prototype.forEach.call(
      document.querySelectorAll('[data-pccc-error="format"]'),
      function (el) { el.hidden = !present || valid; }
    );

    collect(ADD_SELECTORS).forEach(function (btn) {
      btn.disabled = !valid;
      btn.toggleAttribute('data-checkout-disabled', !valid);
    });

    collect(CHECKOUT_SELECTORS).forEach(function (btn) {
      if ('disabled' in btn) btn.disabled = !valid;
      btn.toggleAttribute('data-checkout-disabled', !valid);
    });

    collect(DYNAMIC_SELECTORS).forEach(function (el) {
      if (valid) el.removeAttribute('data-checkout-disabled');
      else el.setAttribute('data-checkout-disabled', 'true');
    });

    // Mutation records are microtasks; this macrotask lifts the guard
    // only after they have been delivered.
    setTimeout(function () { suppress = false; }, 0);
  }

  /* ------------------------------------------------------------------ */

  function init() {
    if (!wrapper()) return;

    if (isLoggedIn()) {
      var first = inputs()[0];
      value = first ? first.value.trim() : '';

      if (isValid()) {
        if (flag('fromAccount')) sync(normalize(value), true);
        else lastSynced = normalize(value);
      } else {
        // A malformed code prefilled from the account metafield or left
        // in the cart: keep it on screen so the format error explains
        // the blocked button, rather than blanking the field silently.
        clearStaleCartAttribute();
      }
    } else {
      var stored = readSession().trim();

      value = stored;

      if (isValid()) {
        sync(normalize(stored), true);
      } else {
        value = '';
        clearSession();
        clearStaleCartAttribute();
      }
    }

    started = true;
    apply();
  }

  function handleEdit(event) {
    var target = event.target;
    if (!target || !target.hasAttribute || !target.hasAttribute('data-pccc-input')) return;

    value = target.value;
    started = true;
    apply();

    if (isValid()) {
      var clean = normalize(value);
      sync(clean);
      if (!isLoggedIn()) writeSession(clean);
    } else {
      sync('');
      if (!isLoggedIn()) clearSession();
    }
  }

  // Tidy the display to the canonical form once they are done typing —
  // rewriting mid-keystroke would jump the caret.
  function handleBlur(event) {
    var target = event.target;
    if (!target || !target.hasAttribute || !target.hasAttribute('data-pccc-input')) return;
    if (!isValid()) return;

    value = normalize(value);
    apply();
  }

  document.addEventListener('input', handleEdit, true);
  document.addEventListener('change', handleEdit, true);
  document.addEventListener('blur', handleBlur, true);

  /* ------------------------------------------------------------------
   * The drawer and the cart section are re-rendered by the theme after
   * every cart change, which replaces the field with server markup. The
   * drawer may also not exist at all on first load, so the first sight
   * of a wrapper has to run init() rather than be ignored.
   * ------------------------------------------------------------------ */
  var repaintTimer = null;

  function ensure() {
    if (!document.querySelector('[data-pccc-wrapper]')) return;

    if (started) apply();
    else init();
  }

  function watch() {
    new MutationObserver(function () {
      if (suppress) return;

      clearTimeout(repaintTimer);
      repaintTimer = setTimeout(ensure, 60);
    }).observe(document.body, { childList: true, subtree: true });

    // Theme editor re-renders sections without a body mutation the
    // observer would catch in time.
    document.addEventListener('shopify:section:load', ensure);
  }

  function start() {
    init();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Back/forward navigation restores form values from bfcache, which
  // would repopulate the field for a guest.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) init();
  });
})();